import { useUserStore } from '@/store/user.store'
import { NestUserRole, type CreateUser, type NestUser } from '@/types/user'
import isEmail from '@rearguard/is-email'
import { computed, ref, type Ref } from 'vue'

export function useUserValidation(user: Ref<NestUser | CreateUser | null>) {
  const submitted = ref(false)
  const userStore = useUserStore()

  const errors = computed<Partial<Record<keyof NestUser, string>>>(() => {
    const data = user.value
    const result: Partial<Record<keyof NestUser, string>> = {}

    if (data?.firstName.trim().length === 0) {
      result.firstName = 'First name cannot be empty'
    }

    if (data?.lastName.trim().length === 0) {
      result.lastName = 'Last name cannot be empty'
    }

    if (!isEmail(data?.email)) {
      result.email = 'Email is invalid'
    }
    const currentUser = userStore.currentNestUser

    if (currentUser == null) throw Error('Cannot validate new user as current user is missing')

    switch (data?.role) {
      case NestUserRole.ADMIN:
        if (currentUser.role !== NestUserRole.ADMIN)
          result.role = 'Admin can only be selected by an admin'
        break
      case NestUserRole.OWNER:
        if (currentUser.role !== NestUserRole.ADMIN && currentUser.role !== NestUserRole.OWNER)
          result.role = 'Owner can only be selected by an admin or owner'
        break
    }

    return result
  })

  const visibleErrors = computed<Partial<Record<keyof NestUser, string>>>(() => {
    if (!submitted.value) return {}
    return errors.value
  })

  const isValid = computed(() => Object.keys(errors.value).length === 0)
  const isSubmittedAndInvalid = computed(() => submitted.value && !isValid.value)

  function validate(): boolean {
    submitted.value = true
    return isValid.value
  }

  return { errors, visibleErrors, isValid, isSubmittedAndInvalid, submitted, validate }
}
